import styled from '@emotion/styled'
import { PopperProps, Popper } from '@mui/material'
import { FC, useEffect, useRef, useState } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { ScrollableContainer } from 'src/components/scrollable-container'
import { Colors, Shapes } from 'src/components/theme'
import { useAfterMountEffect } from 'src/hooks/use-after-mount-effect'

const PADDING = 16
const MAX_WIDTH = 400
export const MAX_CONTENT_WIDTH = MAX_WIDTH - PADDING * 2

const Paper = styled.div`
  box-shadow: ${Colors.BOX_SHADOW};
  margin-left: 12px;
  border-radius: ${Shapes.borderRadius}px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  pointer-events: auto;
  background-color: white;
  border-radius: ${Shapes.borderRadius}px;
  margin-bottom: ${Shapes.spacing[2]}px;
`

const Header = styled.div`
  padding: ${PADDING}px;
  border-bottom: 1px solid ${Colors.neutral100};
`

const Content = styled.div`
  padding: ${PADDING}px;
`

const Arrow = styled.span`
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    left: 0;
    box-sizing: border-box;
    border: 0.7em solid black;
    border-color: transparent transparent white white;
    transform-origin: 0 0;
    transform: rotate(45deg);
    box-shadow: -1px 1px 1px 0 rgba(0, 0, 0, 0.1);
  }
`

export type SidebarResultOverlayProps = {
  open: boolean
  anchorEl: PopperProps['anchorEl']
  header?: JSX.Element
  children?: JSX.Element
  onClose?: () => void
  maxWidth?: number
  contentPadding?: number
}

export const SidebarResultOverlay: FC<SidebarResultOverlayProps> = ({
  open,
  anchorEl,
  header,
  children,
  onClose,
  maxWidth,
  contentPadding,
}) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const arrowRef = useRef<HTMLSpanElement>(null)
  const internalChangedRef = useRef(false)

  // For some reason, the Popper component doesn't update its position and dimensions when the content goes from non-scrollable to scrollable
  // using this ref solves the issue, forcing a repositioning when the content changes
  const { ref: containerRef } = useResizeDetector()

  const handleMouseEnter = () => {
    setInternalOpen(true)
    internalChangedRef.current = true
    setTimeout(() => {
      internalChangedRef.current = false
    }, 200)
  }

  const handleMouseLeave = () => {
    setInternalOpen(false)
  }

  useEffect(() => {
    if (open) {
      setInternalOpen(open)
    } else if (!open && internalOpen && !internalChangedRef.current) {
      setInternalOpen(false)
    }
  }, [open, internalOpen])

  useAfterMountEffect(() => {
    if (!open && !internalOpen) {
      onClose?.()
    }
  }, [open, internalOpen])

  return (
    <Popper
      open={open || internalOpen}
      anchorEl={anchorEl}
      placement="right-start"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        zIndex: 1000,
      }}
      modifiers={[
        {
          name: 'arrow',
          enabled: true,
          options: {
            element: arrowRef.current,
          },
        },
      ]}>
      <Paper>
        <Arrow ref={arrowRef} />
        <Container
          style={{
            maxWidth: maxWidth || MAX_WIDTH,
          }}>
          {header && <Header>{header}</Header>}
          <ScrollableContainer>
            <Content
              style={{
                padding: contentPadding || PADDING,
              }}
              ref={containerRef}>
              {children}
            </Content>
          </ScrollableContainer>
        </Container>
      </Paper>
    </Popper>
  )
}
